import { createSupportTicket } from '@/api/generated/admin/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { toast } from 'sonner';
import styles from './ContactForm.module.scss';

interface ContactFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ContactFormData {
  email: string;
  subject: string;
  message: string;
}

export const ContactForm = ({ isOpen, onOpenChange }: ContactFormProps) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<ContactFormData>({
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.message) {
      toast.error('Please enter your message');
      return;
    }

    setIsSubmitting(true);

    try {
      await createSupportTicket({
        message: formData.message,
        subject: formData.subject || undefined,
        email: formData.email || undefined,
        category: 'general',
        priority: 'medium',
      });

      toast.success(t('footer.help.success'));
      setFormData({ email: '', subject: '', message: '' });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(t('footer.help.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-xl', styles.contactFormDialog)}>
        <DialogHeader>
          <DialogTitle>{t('footer.help.contactUs')}</DialogTitle>
          <DialogDescription>{t('footer.help.sendMessage')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className={styles.formContent}>
          <div className='space-y-2'>
            <Label htmlFor='message'>{t('footer.help.message')}</Label>
            <Textarea
              id='message'
              placeholder={t('footer.help.messagePlaceholder')}
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className={styles.buttonContainer}>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('footer.help.cancel')}
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting ? t('footer.help.sending') : t('footer.help.send')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
